'use client';

import { CustomError, isEnumValue } from '@common/utils';
import { useSearchParams } from 'next/navigation';
import { useState } from 'react';
import { scroller } from 'react-scroll';

import { COMMENT_SORT_TYPE } from '@/api';
import { ROUTES, SEARCH_PARAMS, SELECTOR } from '@/constants';
import { useCustomRouter } from '@/hooks/useCustomRouter';
import { useToastPoint } from '@/hooks/useToastPoint';
import { useAuthStore } from '@/stores/AuthStore';

import { CommentField } from '../molecule/CommentField';
import type { CommentListProps } from '../organism/CommentList';
import { CommentList } from '../organism/CommentList';

export interface CommentListWithFieldProps
  extends Pick<
    CommentListProps,
    'commentType' | 'contentId' | 'initialData' | 'rootCommentId' | 'className'
  > {
  initOnFocus?: boolean;
  policyTalkContentIdx?: string;
  hasCommentField?: boolean;
  commentFieldTopDivider?: boolean;
  commentFieldClassName?: string;
  withPortal?: boolean;
}

export const CommentListWithField = ({
  initOnFocus,
  commentType,
  contentId,
  initialData,
  rootCommentId,
  className,
  policyTalkContentIdx,
  hasCommentField = true,
  commentFieldTopDivider,
  commentFieldClassName,
  withPortal,
}: CommentListWithFieldProps) => {
  const searchParams = useSearchParams();

  const sortType =
    searchParams?.get(SEARCH_PARAMS.SORT_TYPE) || COMMENT_SORT_TYPE.LATEST;

  const parentCommentId = ((params) => {
    if (!params) return undefined;

    const parentCommentId = Number(params);

    if (Number.isNaN(parentCommentId)) return undefined;

    return parentCommentId;
  })(searchParams?.get(SEARCH_PARAMS.PARENT_COMMENT_ID));

  if (!isEnumValue(COMMENT_SORT_TYPE, sortType))
    throw new CustomError({
      return_message: '잘못된 sortType입니다.',
    });

  const isLogin = useAuthStore((state) => state.isLogin);

  const router = useCustomRouter();

  const [placeholder, setPlaceholder] = useState(initialData);

  const isCommentPage = !!parentCommentId;

  const { toastPoint } = useToastPoint();

  return (
    <>
      <CommentList
        className={className}
        commentType={commentType}
        contentId={contentId}
        initialData={placeholder}
        rootCommentId={rootCommentId}
        sortType={sortType}
        onChangeCommentData={setPlaceholder}
        onClickEditButton={(commentId, isRoot) => {
          if (isRoot && policyTalkContentIdx) {
            router.push(
              ROUTES.POLICY_TALK_COMMENT_EDIT.pathname({
                commentId,
                contentIdx: policyTalkContentIdx,
              }),
            );
          } else {
            router.push(
              ROUTES.COMMENT_EDIT.withSearchParams({
                dynamicPath: { commentId },
              }),
            );
          }
        }}
        onClickReplyButton={(commentId, parentCommentId) => {
          const targetRoute = ROUTES.COMMENT_LIST.withSearchParams({
            dynamicPath: { commentType, contentId, commentId },
            searchParams: {
              sortType,
              parentCommentId,
            },
          });

          if (!isLogin) {
            return router.push(
              ROUTES.LOGIN.withSearchParams({
                searchParams: {
                  redirect: targetRoute,
                },
              }),
            );
          }

          return isCommentPage
            ? router.replace(targetRoute)
            : router.push(targetRoute);
        }}
      />
      {hasCommentField && isLogin ? (
        <CommentField
          className={commentFieldClassName}
          commentType={commentType}
          contentId={contentId}
          initOnFocus={initOnFocus}
          parentCommentId={parentCommentId}
          rootCommentId={rootCommentId}
          topDivider={commentFieldTopDivider}
          withPortal={withPortal}
          onSuccess={(res) => {
            if (!res) throw new CustomError();

            if (sortType !== COMMENT_SORT_TYPE.LATEST) {
              const url = new URL(window.location.href);

              url.searchParams.set(
                SEARCH_PARAMS.SORT_TYPE,
                COMMENT_SORT_TYPE.LATEST,
              );

              history.replaceState(null, '', url.search);
            }

            scroller.scrollTo(SELECTOR.COMMENT_LIST_TOP, {
              duration: 300,
              smooth: 'easeOutQuad',
              containerId: SELECTOR.MAIN,
              offset: -100,
            });

            const point = res?.point_activity?.result_yn
              ? res.point_activity.add_points
              : null;

            if (point) {
              toastPoint(point);
            }

            setTimeout(() => {
              scroller.scrollTo(
                SELECTOR.COMMENT_PREFIX + res.wello_comment_id,
                {
                  duration: 300,
                  smooth: 'easeOutQuad',
                  containerId: SELECTOR.MAIN,
                  offset: -100,
                },
              );
            }, 300);
          }}
        />
      ) : null}
    </>
  );
};
